<script lang="ts">
  import { navigate } from 'svelte-routing';
  import { onMount } from 'svelte';
  import {
    isAuthenticated,
    currentUser,
    signIn,
    isLoading,
    needsRegistration,
  } from '$lib/stores/auth';
  import RegisterModal from '$components/common/RegisterModal.svelte';

  let showRegisterModal = false;
  let signInAttempted = false;
  let scrollY = 0;

  onMount(() => {
    const handleScroll = () => {
      scrollY = window.scrollY;
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  });

  $: if (signInAttempted && $isAuthenticated && $currentUser) {
    navigate(`/${$currentUser.username}`);
  }

  $: if (signInAttempted && $needsRegistration) {
    showRegisterModal = true;
  }

  async function handleGetStarted() {
    if ($isAuthenticated && $currentUser) {
      navigate(`/${$currentUser.username}`);
      return;
    }
    try {
      signInAttempted = true;
      await signIn();
    } catch (error) {
      console.error('Sign in failed:', error);
      signInAttempted = false;
    }
  }

  function handleRegistered(event: CustomEvent<{ username: string }>) {
    showRegisterModal = false;
    signInAttempted = false;
    navigate(`/${event.detail.username}`);
  }

  function handleModalClose() {
    showRegisterModal = false;
    signInAttempted = false;
  }

  // Floating cards - more organic placement
  const floatingCards = [
    { id: 1, icon: 'lni lni-user-4', color: 'bg-orange-500', x: 3, y: 8, z: -200, size: 'lg', rotate: -12, duration: 4.2, delay: 0 },
    { id: 2, icon: 'lni lni-x', color: 'bg-neutral-800', x: 82, y: 12, z: -120, size: 'md', rotate: 8, duration: 3.8, delay: 0.5 },
    { id: 3, icon: 'lni lni-youtube', color: 'bg-red-600', x: 88, y: 55, z: -280, size: 'lg', rotate: -6, duration: 5.2, delay: 0.3 },
    { id: 4, icon: 'lni lni-discord', color: 'bg-indigo-600', x: 6, y: 65, z: -180, size: 'md', rotate: 15, duration: 4.5, delay: 0.8 },
    { id: 5, icon: 'lni lni-spotify', color: 'bg-green-600', x: 78, y: 78, z: -150, size: 'sm', rotate: -18, duration: 3.6, delay: 1.1 },
    { id: 6, icon: 'lni lni-twitch', color: 'bg-purple-600', x: 12, y: 38, z: -250, size: 'sm', rotate: 10, duration: 4.8, delay: 0.2 },
    { id: 7, icon: 'lni lni-instagram', color: 'bg-gradient-to-br from-pink-500 to-orange-400', x: 92, y: 32, z: -320, size: 'sm', rotate: -8, duration: 5.5, delay: 0.6 },
    { id: 8, icon: 'lni lni-github', color: 'bg-neutral-700', x: 8, y: 85, z: -100, size: 'md', rotate: 5, duration: 4.1, delay: 1.3 },
  ];

  function getCardStyle(card: typeof floatingCards[0]) {
    const parallaxSpeed = (card.z + 350) / 350;
    const yOffset = scrollY * parallaxSpeed * 0.25;
    const scale = 0.8 + (card.z + 350) / 800;

    return `
      left: ${card.x}%;
      top: ${card.y}%;
      --float-duration: ${card.duration}s;
      --float-delay: ${card.delay}s;
      --parallax-y: ${-yOffset}px;
      --card-rotate: ${card.rotate}deg;
      --card-z: ${card.z}px;
      --card-scale: ${scale};
      opacity: ${0.3 + (card.z + 350) / 700};
    `;
  }

  function getCardSizeClass(size: string) {
    return {
      sm: 'w-12 h-12 text-lg',
      md: 'w-16 h-16 text-xl',
      lg: 'w-20 h-20 text-2xl',
    }[size] || 'w-14 h-14 text-xl';
  }
</script>

<div class="relative min-h-screen bg-neutral-950 overflow-hidden">
  <!-- Subtle background gradient -->
  <div class="absolute inset-0 bg-gradient-to-b from-neutral-900 via-neutral-950 to-neutral-900"></div>

  <!-- Floating cards in background -->
  <div class="fixed inset-0 perspective-[1000px] overflow-hidden pointer-events-none">
    <div class="absolute inset-0 transform-style-3d">
      {#each floatingCards as card (card.id)}
        <div
          class="absolute floating-card"
          style={getCardStyle(card)}
        >
          <div
            class="rounded-xl {card.color} flex items-center justify-center shadow-lg {getCardSizeClass(card.size)}"
          >
            <i class="{card.icon} text-white/90"></i>
          </div>
        </div>
      {/each}
    </div>
  </div>

  <!-- Main content -->
  <div class="relative z-10 min-h-screen flex flex-col lg:flex-row items-center justify-center gap-8 lg:gap-16 px-6 py-12">
    <!-- Left: Text content -->
    <div class="text-center lg:text-left max-w-md">
      <h1 class="text-4xl lg:text-5xl font-bold text-white mb-4 tracking-tight">
        Velf.me
      </h1>
      <p class="text-lg text-neutral-400 mb-6">
        カード型プロフィールサイト<br />
        シンプルで簡単にプロフィールを作ろう
      </p>

      <button
        on:click={handleGetStarted}
        disabled={$isLoading}
        class="inline-flex items-center gap-2 px-6 py-3 bg-orange-500 hover:bg-orange-600 rounded-lg text-white font-medium transition-colors disabled:opacity-50"
      >
        {#if $isLoading}
          <div class="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
          <span>読み込み中...</span>
        {:else if $isAuthenticated}
          <span>マイページへ</span>
          <i class="lni lni-arrow-right"></i>
        {:else}
          <span>始める</span>
          <i class="lni lni-arrow-right"></i>
        {/if}
      </button>

      <p class="mt-4 text-sm text-neutral-500">
        Googleアカウントで無料で作成
      </p>
    </div>

    <!-- Right: Phone mockup with iframe -->
    <div class="phone-mockup">
      <div class="phone-frame">
        <div class="phone-screen">
          <div class="dynamic-island"></div>
          <iframe
            src="/sample?embed=true"
            title="Sample profile"
            class="w-full h-full border-0"
          ></iframe>
        </div>
      </div>
    </div>

    <!-- Footer credits -->
    <div class="absolute bottom-6 left-6 text-neutral-600 text-xs flex items-center gap-4">
      <span>&copy; 2025 Velf.me</span>
      <a href="https://twitter.com/kka_vrc" target="_blank" rel="noopener" class="hover:text-neutral-400 transition-colors">@kka_vrc</a>
      <span>Inspired by <a href="https://bento.me" target="_blank" rel="noopener" class="hover:text-neutral-400 transition-colors">bento.me</a></span>
    </div>
  </div>
</div>

{#if showRegisterModal}
  <RegisterModal
    on:registered={handleRegistered}
    on:close={handleModalClose}
  />
{/if}

<style>
  .perspective-\[1000px\] {
    perspective: 1000px;
  }

  .transform-style-3d {
    transform-style: preserve-3d;
  }

  .floating-card {
    animation: float var(--float-duration, 4s) ease-in-out infinite;
    animation-delay: var(--float-delay, 0s);
    will-change: transform;
  }

  @keyframes float {
    0%, 100% {
      transform:
        translateZ(var(--card-z, 0))
        translateY(calc(var(--parallax-y, 0px) + 0px))
        rotate(var(--card-rotate, 0deg))
        scale(var(--card-scale, 1));
    }
    50% {
      transform:
        translateZ(var(--card-z, 0))
        translateY(calc(var(--parallax-y, 0px) - 15px))
        rotate(calc(var(--card-rotate, 0deg) + 1.5deg))
        scale(var(--card-scale, 1));
    }
  }

  /* Phone mockup */
  .phone-mockup {
    flex-shrink: 0;
  }

  .phone-frame {
    position: relative;
    width: 280px;
    height: 580px;
    background: #1a1a1a;
    border-radius: 40px;
    padding: 12px;
    box-shadow:
      0 0 0 2px #333,
      0 25px 50px -12px rgba(0, 0, 0, 0.5),
      inset 0 0 0 2px #2a2a2a;
  }

  .phone-screen {
    position: relative;
    width: 100%;
    height: 100%;
    background: #0a0a0a;
    border-radius: 32px;
    overflow: hidden;
  }

  .dynamic-island {
    position: absolute;
    top: 12px;
    left: 50%;
    transform: translateX(-50%);
    width: 90px;
    height: 28px;
    background: #000;
    border-radius: 20px;
    z-index: 10;
  }

  @media (max-width: 768px) {
    .phone-frame {
      width: 240px;
      height: 500px;
      border-radius: 32px;
      padding: 10px;
    }

    .dynamic-island {
      width: 72px;
      height: 22px;
      top: 10px;
    }

    .phone-screen {
      border-radius: 26px;
    }
  }
</style>
